import React, { useEffect, useState } from 'react';
import locationIcon from '../../assets/location.svg';
import descriptionIcon from '../../assets/description.svg';
import websiteIcon from '../../assets/web.svg';
import CompanyLogo from './CompanyLogo';

const getHostname = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

const truncateDescription = (text: string, wordCount: number): string => {
  const words = text.split(' ');
  if (words.length <= wordCount) return text;
  return words.slice(0, wordCount).join(' ') + '...';
};

interface CompanyDetailsTooltipProps {
  isVisible: boolean;
  name: string;
  logo?: string;
  website?: string;
  description?: string;
  location?: string;
  className?: string;
}

const CompanyDetailsTooltip: React.FC<CompanyDetailsTooltipProps> = ({
  isVisible,
  name,
  logo,
  website = '',
  description,
  location,
  className = ''
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isScrolling, setIsScrolling] = useState(false);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const tableRef = React.useRef<HTMLElement | null>(null);
  let scrollTimeout: number;

  useEffect(() => {
    if (tooltipRef.current) {
      const table = tooltipRef.current.closest('.overflow-y-auto');
      if (table) {
        tableRef.current = table as HTMLElement;
      }
    }
  }, []);

  const handleScroll = () => {
    setIsScrolling(true);
    clearTimeout(scrollTimeout);
    scrollTimeout = window.setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (tooltipRef.current && !isScrolling && tableRef.current) {
      const tooltip = tooltipRef.current;
      const table = tableRef.current;
      const tooltipWidth = tooltip.offsetWidth;
      const tooltipHeight = tooltip.offsetHeight;
      const padding = 10;
      const cursorOffset = 15;

      const tableRect = table.getBoundingClientRect();
      let x = e.clientX + cursorOffset;
      let y = e.clientY + cursorOffset;

      if (x + tooltipWidth > tableRect.right - padding) {
        x = e.clientX - tooltipWidth - cursorOffset;
      }
      if (y + tooltipHeight > tableRect.bottom - padding) {
        y = e.clientY - tooltipHeight - cursorOffset;
      }
      if (x < tableRect.left + padding) x = tableRect.left + padding;
      if (y < tableRect.top + padding) y = tableRect.top + padding;

      const pageX = x + window.pageXOffset;
      const pageY = y + window.pageYOffset;

      setPosition({ x: pageX, y: pageY });
    }
  };

  useEffect(() => {
    if (isVisible) {
      window.addEventListener('mousemove', handleMouseMove);
      if (tableRef.current) {
        tableRef.current.addEventListener('scroll', handleScroll, true);
      }
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      if (tableRef.current) {
        tableRef.current.removeEventListener('scroll', handleScroll, true);
      }
      clearTimeout(scrollTimeout);
    };
  }, [isVisible, isScrolling]);

  if (!isVisible || isScrolling) return null;

  return (
    <div
      ref={tooltipRef}
      className={`fixed z-50 pointer-events-none animate-fadeIn ${className}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`
      }}
    >
      <div className="bg-[#FAFAFA] rounded-[16px] shadow-lg max-w-[380px] border border-[#EEEEEE] overflow-hidden">
        {/* Header Section */}
        <div className="p-4 border-b border-[#EEEEEE]">
          <div className="flex items-center gap-3">
            <CompanyLogo
              name={name}
              website={website}
              existingLogo={logo}
              className="w-[28px] h-[28px] rounded-lg shadow-sm"
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-[#222222] font-normal text-[16px] leading-tight truncate">
                {name}
              </h3>
            </div>
          </div>
        </div>

        {/* Details Table Section */}
        <div className="p-4">
          <table className="w-full">
            <tbody>
              {description && (
                <tr>
                  <td className="w-[24px] pr-2 align-top pt-3">
                    <div className="w-[14px] h-[14px] flex items-center justify-center mt-[3px]">
                      <img src={descriptionIcon} alt="description" className="w-full h-full" />
                    </div>
                  </td>
                  <td className="text-[14px] font-normal text-[#222222] leading-relaxed py-2">
                    {truncateDescription(description, 10)}
                  </td>
                </tr>
              )}
              {location && (
                <tr>
                  <td className="w-[24px] pr-2 align-top pt-3">
                    <div className="w-[14px] h-[14px] flex items-center justify-center mt-[3px]">
                      <img src={locationIcon} alt="location" className="w-full h-full" />
                    </div>
                  </td>
                  <td className="text-[14px] font-normal text-[#222222] truncate pt-3">
                    {location}
                  </td>
                </tr>
              )}
              {website && (
                <tr>
                    <td className="w-[24px] pr-2 align-top pt-3">
                    <div className="w-[14px] h-[14px] flex items-center justify-center mt-[3px]">
                      <img src={websiteIcon} alt="website" className="w-full h-full" />
                    </div>
                  </td>
                  <td className="py-2">
                    <a 
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#5D90D4] text-[14px] hover:underline rounded-[8px] border border-[#A7CAE9] px-2 py-[4px] truncate inline-block"
                      onClick={(e) => e.stopPropagation()}
                    >
                      www.{getHostname(website)}
                    </a>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsTooltip; 